/**
 * CDM Policy Proposals — review UI for AI-suggested tuning proposals.
 *
 * Per ADR-0006 (AI-suggested human-overridden tuning):
 *   - Every proposal carries evidence + replay diff
 *   - Approve: applies as a new versioned policy row
 *   - Reject: feedback logged for the agent's next cycle
 *
 * No silent autotune — humans decide on every proposal.
 */
import { useEffect, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import {
  Brain, ChevronLeft, Check, X, RefreshCw, AlertTriangle,
  ArrowRight, Clock,
} from 'lucide-react'
import { clsx } from 'clsx'
import { api } from '../lib/api'

interface Proposal {
  id: string
  target: string
  target_id: string
  proposed_change: any
  evidence: any
  replay_diff: any
  proposed_by: string
  proposer_reasoning: string | null
  critic_review: any
  state: string
  created_at: string | null
  decided_by: string | null
  decided_at: string | null
  decision_notes: string | null
}

const STATE_TONE: Record<string, string> = {
  open:      'bg-amber/15 text-amber-light border-amber/30',
  approved:  'bg-emerald/15 text-emerald border-emerald/30',
  rejected:  'bg-rose/15 text-rose-light border-rose/30',
  auto_dismissed: 'bg-text-muted/15 text-text-muted border-border',
  superseded:     'bg-text-muted/15 text-text-muted border-border',
}

export default function CdmPolicyProposalsPage() {
  const [proposals, setProposals] = useState<Proposal[]>([])
  const [stateFilter, setStateFilter] = useState<string>('open')
  const [searchParams] = useSearchParams()
  const [targetFilter, setTargetFilter] = useState<string>(searchParams.get('target') || '')
  const [loading, setLoading] = useState(false)
  const [running, setRunning] = useState(false)
  const [message, setMessage] = useState('')

  const load = () => {
    setLoading(true)
    api.get('/api/cdm/policy-proposals', { params: {
      state: stateFilter || undefined,
      target: targetFilter || undefined,
      limit: 100,
    }})
      .then(r => setProposals(r.data?.proposals || []))
      .catch(() => {})
      .finally(() => setLoading(false))
  }
  useEffect(() => { load() }, [stateFilter, targetFilter])

  const decide = async (id: string, decision: 'approved' | 'rejected') => {
    const notes = decision === 'rejected'
      ? window.prompt('Reason for rejecting? (will be logged for the agent)')
      : null
    if (decision === 'rejected' && notes === null) return  // user cancelled
    try {
      await api.post(`/api/cdm/policy-proposals/${id}/decide`, null, {
        params: { decision, notes: notes || undefined },
      })
      setMessage(`Proposal ${decision}.`)
      load()
    } catch (e: any) {
      setMessage(`Failed: ${e.response?.data?.detail || 'unknown'}`)
    }
  }

  const runTuner = async () => {
    setRunning(true)
    setMessage('Running survivorship tuning agent…')
    try {
      const { data } = await api.post('/api/cdm/policy-proposals/agent/survivorship/run')
      setMessage(`Tuner: ${data.evidence_count} (archetype, field) tuples reviewed, ${data.proposals_drafted} proposals drafted, ${data.proposals_inserted} ratified by Critic.`)
      load()
    } catch (e: any) {
      setMessage(`Tuner: ${e.response?.data?.detail || 'failed'}`)
    } finally {
      setRunning(false)
    }
  }

  return (
    <div className="space-y-4 animate-fade-in">
      <div className="flex items-baseline gap-4 pb-2 border-b border-border flex-wrap">
        <Link to="/app/cdm" className="text-text-muted hover:text-text-primary inline-flex items-center gap-1">
          <ChevronLeft size={14} /> CDM
        </Link>
        <h1 className="text-lg font-bold text-text-primary flex items-center gap-2">
          <Brain size={18} className="text-purple-light" /> POLICY PROPOSALS
        </h1>
        <span className="text-[11px] uppercase tracking-widest text-text-muted">
          AI-suggested · human-approved (ADR-0006)
        </span>
        <div className="ml-auto flex items-center gap-2">
          <button type="button" onClick={runTuner} disabled={running} className="btn-ghost"
                  title="Run the survivorship tuning agent now (admin only)">
            <Brain size={11} /> Run tuner
          </button>
          <button type="button" onClick={load} disabled={loading} className="btn-ghost">
            <RefreshCw size={11} className={loading ? 'animate-spin' : ''} /> Refresh
          </button>
        </div>
      </div>

      {message && (
        <div className="text-[11px] text-text-secondary font-mono px-3 py-2 border border-border rounded bg-surface-card">
          {message}
        </div>
      )}

      <div className="flex items-center gap-2 text-xs flex-wrap">
        <span className="text-text-muted">State:</span>
        {['open', 'approved', 'rejected', ''].map(s => (
          <button key={s || 'all'} type="button" onClick={() => setStateFilter(s)}
            className={clsx(
              'px-2 py-1 rounded border',
              stateFilter === s
                ? 'bg-blue/10 text-blue-light border-blue/30'
                : 'border-border text-text-secondary hover:bg-surface-raised'
            )}>
            {s || 'all'}
          </button>
        ))}
        <span className="text-text-muted ml-3">Target:</span>
        {['', 'survivorship', 'orphan_owner'].map(t => (
          <button key={t || 'all-targets'} type="button" onClick={() => setTargetFilter(t)}
            className={clsx(
              'px-2 py-1 rounded border',
              targetFilter === t
                ? 'bg-purple/10 text-purple-light border-purple/30'
                : 'border-border text-text-secondary hover:bg-surface-raised'
            )}>
            {t || 'all'}
          </button>
        ))}
        <span className="ml-auto text-text-muted">{proposals.length} proposals</span>
      </div>

      <div className="space-y-3">
        {proposals.length === 0 && (
          <div className="card p-6 text-center text-text-muted text-sm">
            No proposals in the {stateFilter || 'any'} state.
            {stateFilter === 'open' && ' Click "Run tuner" to scan triage history.'}
          </div>
        )}
        {proposals.map(p => (
          <ProposalCard key={p.id} p={p} onDecide={decide} />
        ))}
      </div>
    </div>
  )
}


function ProposalCard({ p, onDecide }: { p: Proposal; onDecide: (id: string, d: 'approved' | 'rejected') => void }) {
  const change = p.proposed_change || {}
  const ev = p.evidence || {}
  const critic = p.critic_review || {}

  return (
    <div className="card p-4 space-y-3">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className="badge text-[9px] bg-purple/10 text-purple-light border border-purple/30">{p.target}</span>
            <code className="font-mono text-xs text-text-primary font-semibold">{p.target_id}</code>
            <span className={clsx('badge text-[9px] border', STATE_TONE[p.state] || 'border-border text-text-muted bg-surface-raised')}>
              {p.state}
            </span>
            <span className="text-[10px] text-text-muted ml-auto inline-flex items-center gap-1">
              <Clock size={10} /> {p.created_at ? new Date(p.created_at).toLocaleString() : '—'}
            </span>
          </div>
          {p.proposer_reasoning && (
            <p className="text-[11px] text-text-secondary leading-snug">{p.proposer_reasoning}</p>
          )}
        </div>
      </div>

      {/* Proposed change — priority-list diff (survivorship target) */}
      {change.current_priority && change.proposed_priority && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          <div className="rounded border border-border p-2 bg-surface-card">
            <div className="text-[9px] uppercase tracking-widest text-text-muted mb-1">Current priority</div>
            <div className="flex flex-wrap gap-1">
              {change.current_priority.map((s: string, i: number) => (
                <span key={i} className="badge text-[10px] bg-surface-raised text-text-secondary border border-border">
                  {i + 1}. {s}
                </span>
              ))}
            </div>
          </div>
          <div className="rounded border border-emerald/30 p-2 bg-emerald/5">
            <div className="text-[9px] uppercase tracking-widest text-emerald mb-1">Proposed priority</div>
            <div className="flex flex-wrap gap-1">
              {change.proposed_priority.map((s: string, i: number) => {
                const wasAt = change.current_priority.indexOf(s)
                const moved = wasAt !== i
                return (
                  <span key={i} className={clsx(
                    'badge text-[10px] border',
                    moved ? 'bg-emerald/10 text-emerald border-emerald/30' : 'bg-surface-raised text-text-secondary border-border'
                  )}>
                    {i + 1}. {s} {moved && wasAt >= 0 && <span className="text-[9px] opacity-70">(was {wasAt + 1})</span>}
                  </span>
                )
              })}
            </div>
          </div>
        </div>
      )}

      {/* Proposed change — orphan_owner inference */}
      {p.target === 'orphan_owner' && change.inferred_owner && (
        <div className="rounded border border-emerald/30 p-2 bg-emerald/5">
          <div className="text-[9px] uppercase tracking-widest text-emerald mb-1">Inferred owner</div>
          <div className="flex items-center gap-2">
            <span className="badge text-xs bg-emerald/15 text-emerald border border-emerald/40 font-bold">
              {change.inferred_owner}
            </span>
            <span className="text-[10px] text-text-muted">
              for NHI {change.user_id?.slice(0, 8)}… ({change.kind})
            </span>
            <span className="ml-auto text-[10px] font-mono">
              confidence <span className={clsx(
                change.confidence >= 0.7 ? 'text-emerald' :
                change.confidence >= 0.4 ? 'text-amber-light' : 'text-rose-light'
              )}>{(change.confidence * 100).toFixed(0)}%</span>
            </span>
          </div>
        </div>
      )}

      {/* Evidence — orphan signals */}
      {p.target === 'orphan_owner' && Array.isArray(ev.signals) && ev.signals.length > 0 && (
        <div className="rounded border border-border p-2 bg-surface-card">
          <div className="text-[9px] uppercase tracking-widest text-text-muted mb-1">Signals ({ev.signals.length})</div>
          <div className="space-y-1">
            {ev.signals.map((s: any, i: number) => (
              <div key={i} className="flex items-center gap-2 text-[11px]">
                <span className="badge text-[9px] bg-purple/10 text-purple-light border border-purple/30 w-20 text-center">
                  {s.kind}
                </span>
                <span className="text-text-secondary flex-1 truncate">{s.evidence}</span>
                <span className="text-text-muted">→</span>
                <span className="font-mono text-text-primary">{s.team_guess}</span>
                <span className="font-mono text-[10px] text-text-muted">w={s.weight}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Evidence — survivorship counts */}
      {p.target === 'survivorship' && (
        <div className="grid grid-cols-3 gap-3 text-[11px]">
          <div>
            <div className="text-[9px] uppercase tracking-widest text-text-muted">Acked-noise events</div>
            <div className="font-mono text-amber-light text-base">{ev.ack_noise_count ?? 0}</div>
          </div>
          <div>
            <div className="text-[9px] uppercase tracking-widest text-text-muted">Acked-real events</div>
            <div className="font-mono text-emerald text-base">{ev.ack_real_count ?? 0}</div>
          </div>
          <div>
            <div className="text-[9px] uppercase tracking-widest text-text-muted">Window</div>
            <div className="font-mono text-text-secondary text-base">{ev.window_days ?? 30} days</div>
          </div>
        </div>
      )}

      {/* Replay diff */}
      {p.replay_diff && (
        <div className="text-[11px] border border-border rounded px-2 py-1.5 bg-surface-card">
          <span className="text-text-muted">Replay diff: </span>
          <span className="text-text-primary">
            {p.replay_diff.evaluated_records ?? 0} records · {p.replay_diff.values_changed ?? 0} change · {p.replay_diff.critical_flips ?? 0} critical
          </span>
          {p.replay_diff.note && <span className="text-text-muted italic"> — {p.replay_diff.note}</span>}
        </div>
      )}

      {/* Critic review */}
      {critic && Object.keys(critic).length > 0 && (
        <div className={clsx(
          'text-[11px] border rounded px-2 py-1.5',
          critic.ratify ? 'border-emerald/30 bg-emerald/5' : 'border-amber/30 bg-amber/5'
        )}>
          <span className={critic.ratify ? 'text-emerald' : 'text-amber-light'}>
            Critic: {critic.ratify ? 'ratified' : 'flagged'}
          </span>
          {critic.reasoning && <span className="text-text-secondary"> — {critic.reasoning}</span>}
        </div>
      )}

      {/* Action buttons */}
      {p.state === 'open' && (
        <div className="flex items-center gap-2">
          <button type="button" onClick={() => onDecide(p.id, 'approved')}
            className="btn-ghost border-emerald/30 text-emerald hover:bg-emerald/10">
            <Check size={11} /> Approve
          </button>
          <button type="button" onClick={() => onDecide(p.id, 'rejected')}
            className="btn-ghost border-rose/30 text-rose-light hover:bg-rose/10">
            <X size={11} /> Reject
          </button>
          <span className="text-[10px] text-text-muted ml-auto inline-flex items-center gap-1">
            <AlertTriangle size={10} /> Approval applies a new versioned policy row
          </span>
        </div>
      )}

      {p.state !== 'open' && p.decided_by && (
        <div className="text-[10px] text-text-muted">
          {p.state} by {p.decided_by} {p.decided_at && `at ${new Date(p.decided_at).toLocaleString()}`}
          {p.decision_notes && <div className="mt-1 italic">"{p.decision_notes}"</div>}
        </div>
      )}
    </div>
  )
}
